import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaExternalLinkAlt, FaGraduationCap, FaUniversity } from 'react-icons/fa';
import useAnalytics from '../../hooks/useAnalytics';

const WetsusBanner = ({ className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

  // Check if banner was previously dismissed
  React.useEffect(() => {
    const isDismissed = localStorage.getItem('wetsus-banner-dismissed');
    if (!isDismissed) {
      setIsVisible(true);
    }
  }, []);

  // Track banner impression when component mounts and is visible
  // React.useEffect(() => {
  //   if (isVisible) {
  //     trackEvent({
  //       category: 'Wetsus Banner',
  //       action: 'Impression',
  //       label: 'Banner Displayed'
  //     });
  //   }
  // }, [isVisible, trackEvent]);

  const handleClose = () => {
    setIsVisible(false);
    // Save dismissal state to localStorage
    localStorage.setItem('wetsus-banner-dismissed', 'true');
    trackEvent({
      category: 'Wetsus Banner',
      action: 'Close',
      label: 'Banner Dismissed'
    });
  };

  const handleViewOpportunities = () => {
    trackEvent({
      category: 'Wetsus Banner',
      action: 'Click',
      label: 'View Wetsus Opportunities',
      value: 1
    });
    
    // Additional tracking for conversion funnel
    trackEvent({
      category: 'Wetsus Conversion',
      action: 'Internal Link Click',
      label: 'PhD Vacancies Page'
    });
  };

  const handleLearnMore = () => {
    trackEvent({
      category: 'Wetsus Banner',
      action: 'Click',
      label: 'Learn More About Wetsus',
      value: 1
    });
    
    // Additional tracking for external link
    trackEvent({
      category: 'Wetsus Conversion',
      action: 'External Link Click',
      label: 'Official Wetsus Website'
    });
  };

  const handleLogoClick = () => {
    trackEvent({
      category: 'Wetsus Banner',
      action: 'Logo Click',
      label: 'Wetsus Logo Interaction'
    });
  };

  // Function to reset banner (useful for testing or admin purposes)
  const resetBanner = () => {
    localStorage.removeItem('wetsus-banner-dismissed');
    setIsVisible(true);
  };

  // Expose reset function to window for testing (only in development)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.resetWetsusBanner = resetBanner;
    }
  }, []);

  if (!isVisible) return null;

  return (
    <div className={`w-full bg-gradient-to-r from-[#FFEEED] to-[#FFD6D4] text-[#1B222A] shadow-lg ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-3 lg:space-x-4 flex-1">
            {/* Wetsus Logo */}
            <div className="flex-shrink-0">
              <div 
                className="w-12 h-12 lg:w-14 lg:h-14 bg-white rounded-lg flex items-center justify-center shadow-sm cursor-pointer hover:shadow-md transition-shadow duration-200"
                onClick={handleLogoClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleLogoClick();
                  }
                }}
                aria-label="Wetsus Research Center Logo"
              >
                <img
                  src="/university/Wetsus.png"
                  alt="Wetsus Research Center"
                  className="w-8 h-8 lg:w-10 lg:h-10 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <FaUniversity className="w-6 h-6 lg:w-8 lg:h-8 text-[#DB4F40] hidden" />
              </div>
            </div>
            
            {/* Banner Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-base lg:text-lg font-bold text-[#1B222A] mb-1 leading-tight">
                🌟 Featured Partner: Wetsus Research Center
              </h3>
              <p className="text-xs lg:text-sm text-[#1B222A]/80 leading-relaxed">
                <strong>10 brand new PhD positions</strong> now available! Discover cutting-edge opportunities in water technology and sustainable research at Wetsus, Netherlands
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 lg:flex-shrink-0">
            <Link
              to="/phd-vacancies/standard/Netherlands/Wetsus"
              onClick={handleViewOpportunities}
              className="inline-flex items-center justify-center px-3 py-2 lg:px-4 lg:py-2 bg-[#DB4F40] text-white rounded-lg font-semibold text-xs lg:text-sm hover:bg-[#C64537] transition-colors duration-200 shadow-sm hover:shadow-md whitespace-nowrap"
            >
              <FaGraduationCap className="w-3 h-3 lg:w-4 lg:h-4 mr-1.5 lg:mr-2" />
              View Opportunities
              <FaExternalLinkAlt className="w-2.5 h-2.5 lg:w-3 lg:h-3 ml-1.5 lg:ml-2" />
            </Link>
            
            <a
              href="https://phdpositionswetsus.eu/available-research-position/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLearnMore}
              className="inline-flex items-center justify-center px-3 py-2 lg:px-4 lg:py-2 bg-white/80 text-[#1B222A] rounded-lg font-semibold text-xs lg:text-sm hover:bg-white transition-colors duration-200 border border-white/50 whitespace-nowrap"
            >
              Learn More
              <FaExternalLinkAlt className="w-2.5 h-2.5 lg:w-3 lg:h-3 ml-1.5 lg:ml-2" />
            </a>
          </div>
          
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 lg:relative lg:top-0 lg:right-0 lg:ml-4 p-2 text-[#1B222A]/70 hover:text-[#1B222A] hover:bg-white/20 rounded-lg transition-colors duration-200"
            aria-label="Close banner"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WetsusBanner;
